<script setup>
import {computed, watch, nextTick} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useComponentsStore} from '@/stores/components'
import {useStateHistoryStore} from '@/stores/stateHistory'
import {useModifiedItemsStore} from '@/stores/modifiedItems'
import {useDocumentTitleStore} from '@/stores/documentTitle'
import {useUserStore} from '@/stores/user'
import {useAppStore} from '@/stores/appStore'
import api from '@/services/api'
import AjaxLoading from './common/AjaxLoading'
import Navigation from './common/navigation/Navigation'
import LoginForm from './common/LoginForm'
import Support from './common/support/Support'
import Modals from './vue/modals/Modals'
import Toasts from "./common/toasts/Toasts"
import UserProfileOffcanvas from "./common/user-profile/UserProfileOffcanvas.vue"

const route = useRoute()
const router = useRouter()

const appStore = useAppStore()
const componentsStore = useComponentsStore()
const stateHistoryStore = useStateHistoryStore()
const modifiedItemsStore = useModifiedItemsStore()
const documentTitleStore = useDocumentTitleStore()
const userStore = useUserStore()

const user = computed(() => userStore.user)
const userExists = computed(() => userStore.exists)
const tableName = computed(() => route.params.component)
const componentId = computed(() => componentsStore.componentIds[tableName.value])
const access = computed(() => userStore.access(tableName.value))

const createFavIconLinkTags = () => {
    let link = document.createElement('link')
    link.setAttribute('rel', 'apple-touch-icon')
    link.setAttribute('href', '/apple-touch-icon.png')
    link.setAttribute('sizes', '180x180')
    document.head.appendChild(link)

    link = document.createElement('link')
    link.setAttribute('rel', 'icon')
    link.setAttribute('type', 'image/png')
    link.setAttribute('href', '/favicon-32x32.png')
    link.setAttribute('sizes', '32x32')
    document.head.appendChild(link)

    link = document.createElement('link')
    link.setAttribute('rel', 'icon')
    link.setAttribute('type', 'image/png')
    link.setAttribute('href', '/favicon-16x16.png')
    link.setAttribute('sizes', '16x16')
    document.head.appendChild(link)
}

const setRouteProperties = () => {
    const name = route.name
    const component = route.params.component
    const id = route.params.id

    stateHistoryStore.set({
        stateName: name,
        tableName: component,
        id: id,
        fullPath: route.fullPath
    })

    documentTitleStore.set({
        stateName: name,
        tableName: component,
        itemId: id,
    })
}

const tokenExists = () => {
    return localStorage.getItem('token')
}

const init = async () => {
    const token = localStorage.getItem('token')

    if (token) {
        // 1. Fetch current user
        const userResponse = await api.get('api/users/get-current-user')
        const userObj = userResponse.data

        // 2. Fetch user access levels
        const accessResponse = await api.get(`api/user/${userObj.id}/access`)
        userObj.access = accessResponse.data

        // 3. Update store state and apply dark mode via Pinia user store
        await userStore.set(userObj)
        await userStore.applyDarkMode()
    }
}

const userAccess = () => {
    // Admin only route restriction
    const adminRouteNames = ['themesEditor']
    if (
        adminRouteNames.includes(route.name)
        && !user.value?.administrator
    ) {
        console.error("Access denied! Only administrators have access to the '" + route.name + "' route.")
        router.push({name: 'dashboard'})
        return
    }

    if (
        user.value
        && tableName.value
    ) {
        let id = parseInt(route.params.id)
        let component = componentsStore.items.find(o => o.id === componentId.value)

        if (
            tableName.value === 'users'
            && id === user.value.id
        ) {
            // Allow users to edit their own user account, even if they haven't access to the users tableName.
        } else if (
            user.value.access?.__formAdmin
            && component?.navigationGroup === 'Forms'
        ) {
            // Context placeholder for form access validation logic
        }

        // No access to the component whatsoever
        else if (!access.value) {
            nextTick(() => {
                console.log('ss')
                router.push({name: 'dashboard'})
            })
        }

        // If an attempt is made to edit or add without permissions then redirect to the listing.
        else if (!!id) {
            let privilege = (id === 0) ? 'add' : 'edit'
            if (!access.value[privilege]) {
                console.log("'" + privilege + "' privilege denied!")
                router.push({name: 'list', params: {component: tableName.value}})
            }

        } else {
            // devOnly component restriction.
            if (
                component
                && component.devOnly
                && !appStore.betaMode
            ) {
                router.push({name: 'dashboard'})
            }
        }
    }
}

// Redirect erroneous legacy AngularJS URLs to the dashboard.
if (window.location.href.indexOf('/#/!/') > -1) {
    window.location = window.location.href.split('#')[0]
}

appStore.setParams()
    .then(() => {
        return init()
    })
    .then(() => {
        userAccess()
    })

createFavIconLinkTags()
setRouteProperties()

watch(
    () => route.fullPath,
    async (newPath, oldPath) => {
        await modifiedItemsStore.clearModifiedItems()
        setRouteProperties()
        userAccess()
    }
)
</script>

<template>
    <div class="d-flex flex-column h-100">
        <div v-if="userExists" class="main-container d-flex flex-column h-100">
            <AjaxLoading/>

            <div class="d-flex h-100 overflow-auto flex-column">
                <Navigation/>

                <div class="main-content flex-grow-1">
                    <router-view/>
                </div>
            </div>

            <Support/>

            <UserProfileOffcanvas/>

            <Modals/>
        </div>
        <div v-else-if="!userExists && tokenExists()" class="ajax-spinner">
            <span class="ajax-spinner-icon"/>
        </div>

        <LoginForm v-else-if="!userExists"/>

        <Toasts/>
    </div>
</template>

<style scoped>
.main-content {
    overflow-y: auto;
}

.ajax-spinner {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}
</style>